import React, { useEffect, useMemo, useState } from 'react'
import useTabParam from '../../hooks/useTabParam'
import { Box, Chip, CircularProgress, MenuItem, Select, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import Page from '../../components/Page'
import PageTitle from '../../components/PageTitle'
import { fetchVM } from './engine'
import P1KamSam from './P1KamSam'
import P2Shipments from './P2Shipments'
import P4Sales from './P4Sales'
import P5Top50 from './P5Top50'

/* ─── Design tokens ────────────────────────────────────────────────────────── */
// Matches the app-wide theme (src/theme/palette.js) — same values used by the
// rest of the admin panel (e.g. Forward Shipments under Shipment Management),
// not a page-local palette.
export const PRIMARY       = '#2065D1'
export const PRIMARY_DARK  = '#103996'
export const PRIMARY_LIGHT = '#D1E9FC'
export const PRIMARY_MID   = '#DFE3E8'
export const WARNING       = '#B78103'
export const WARNING_LIGHT = '#FFF7CD'
export const GREEN        = '#047857'
export const RED          = '#B72136'

/* ─── Shared number formatting ─────────────────────────────────────────────── */
export const nf = n => (n == null || isNaN(n)) ? '—' : Math.round(n).toLocaleString('en-IN')
const pv = v => (v == null || isNaN(v)) ? null : Math.round(v)
const pc = v => { const r = pv(v); return r == null ? '—' : (r > 0 ? '+' : '') + r + '%' }

/**
 * Signed percentage. `size` defaults to the in-table size; pass 'inherit' when
 * it is the headline value of a Kpi so it matches the plain numbers instead of
 * rendering at half their size.
 */
export function Pct({ v, bold = true, size = '0.82rem' }) {
  const r = pv(v)
  if (r == null) return <span style={{ color: '#bbb', fontSize: size }}>—</span>
  const color = r > 0 ? GREEN : r < 0 ? RED : '#999'
  return <span style={{ fontWeight: bold ? 700 : 500, color, fontSize: size }}>{pc(v)}</span>
}

/**
 * The KPI tile used by every tab. Previously each tab carried its own copy and
 * they had drifted apart, which is what leadership was seeing: values sat at
 * different heights and percentages rendered smaller than plain numbers.
 *
 * Two things keep the row visually even and must not be dropped:
 *  - the header has a fixed minHeight, because the icon chip is ~25px tall
 *    against ~14px for a bare label, so cards with an icon pushed their value
 *    down relative to cards without one;
 *  - the sub-line is always rendered (empty when absent) so a card that has no
 *    sub-text doesn't sit at a different height from its neighbours.
 */
export function Kpi({ label, value, sub, color = PRIMARY, icon: Icon }) {
  return (
    <Box sx={{
      background: '#fff', borderRadius: '12px', borderTop: `3px solid ${color}`,
      p: '12px 14px', height: '100%',
      display: 'flex', flexDirection: 'column',
      boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
      transition: 'box-shadow 0.2s, transform 0.2s',
      '&:hover': { boxShadow: `0 6px 20px ${color}22`, transform: 'translateY(-1px)' },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, minHeight: 26, mb: 0.75 }}>
        <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.55, color: '#9a9ab0', lineHeight: 1.25 }}>
          {label}
        </Typography>
        {Icon && (
          <Box sx={{ p: 0.6, borderRadius: '8px', background: color + '18', display: 'flex', flexShrink: 0 }}>
            <Icon sx={{ fontSize: 15, color }} />
          </Box>
        )}
      </Box>
      {/* mt:auto pins the value+sub block to the bottom of the tile. Cards in a
          row are equal height, so the values line up even when one label wraps
          to more lines than its neighbours' — which is what happens on narrow
          screens and is otherwise enough to knock a value out of alignment. */}
      <Box sx={{ mt: 'auto' }}>
        <Typography component='div' sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1a1a2e', lineHeight: 1.15, letterSpacing: '-0.02em' }}>
          {value}
        </Typography>
        {/* Held to exactly one line. Free-wrapping sub-text was the second thing
            knocking values out of line: the block is bottom-pinned, so a sub that
            wrapped to two lines lifted its value above its neighbours'. */}
        <Typography component='div' sx={{
          fontSize: '0.72rem', color: '#9a9ab0', mt: 0.25, lineHeight: 1.4,
          height: '1.4em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {sub != null ? sub : ''}
        </Typography>
      </Box>
    </Box>
  )
}

/* ─── Filter helpers ───────────────────────────────────────────────────────── */
export function matchRow(row, f) {
  if (f.seg !== 'ALL' && row.seg !== f.seg)                                   return false
  if (f.kam !== 'ALL' && row.kam !== f.kam)                                   return false
  if (f.sam !== 'ALL' && row.sam !== f.sam)                                   return false
  if (f.search && !row.name?.toLowerCase().includes(f.search.toLowerCase())) return false
  if (f.chips.has('degrowth') && !(row.may > 0 && row.proj < row.may))       return false
  if (f.chips.has('priority') && !row.priority)                               return false
  if (f.chips.has('active')   && !(row.jun > 0))                             return false
  if (f.chips.has('inactive') && row.jun > 0)                                return false
  if (f.chips.has('behind')   && !(row.gMJ != null && row.gMJ < f.goal))     return false
  return true
}

// Internal placeholder tags need a readable label for ops; the underlying
// name/value (used for filter matching and Select value) stays unchanged —
// this only affects what's rendered as visible text.
export function displayName(name) {
  if (name === '(direct)') return 'No SAM (direct)'
  return name
}

export function scopeTotals(vm, f) {
  if (f.sam !== 'ALL') return (vm.sams || []).find(s => s.name === f.sam) || vm.overall.total
  if (f.kam !== 'ALL') {
    const k = vm.kams.find(k => k.name === f.kam) || {}
    if (f.seg === 'ENT') return k.entAgg || {}
    if (f.seg === 'SME') return k.smeAgg || {}
    return k
  }
  if (f.seg === 'ENT') return vm.overall.ent
  if (f.seg === 'SME') return vm.overall.sme
  return vm.overall.total
}

/* ─── Quick chips ──────────────────────────────────────────────────────────── */
const CHIPS = [
  { id: 'degrowth', label: '▼ Degrowth', danger: true  },
  { id: 'priority', label: '★ Priority',  danger: false },
  { id: 'active',   label: 'Active',      danger: false },
  { id: 'inactive', label: 'Inactive',    danger: false },
  { id: 'behind',   label: '⚠ Behind goal', danger: true },
]

/**
 * Chips hidden per tab (ops feedback: these read as noise on the KAM/SAM page,
 * where the leaderboard already surfaces degrowth and activity per row).
 * Anything listed here is also CLEARED when the tab is entered — a hidden chip
 * that stayed in the filter set would keep filtering with no visible control
 * explaining why the numbers look wrong.
 */
const HIDDEN_CHIPS_BY_TAB = {
  // KAM/SAM shows no quick-chips at all (leadership feedback). Beyond the ask,
  // keeping Priority alone here would have been misleading: of the five chips
  // only `behind` ever filtered the KAM leaderboard, so once that went the
  // remaining chip changed the WoW/DoD charts and nothing else on the page.
  '1': ['degrowth', 'priority', 'active', 'inactive', 'behind'],
}

// Team & Mapping moved to Merchant Setup → Prozo Brand Personnel Mapping
// (KAM_SAM_SL_MONGO_MAPPING_DESIGN.md) — this dashboard is read-only now.
const TABS = [
  { value: '1', label: 'KAM / SAM' },
  { value: '2', label: 'Shipment Overview' },
  { value: '3', label: 'Sales Leadership' },
  { value: '4', label: 'Top 50 Clients' },
]

/* ─── Compact select ───────────────────────────────────────────────────────── */
function FSelect({ value, onChange, children, minWidth = 140 }) {
  return (
    <Select
      value={value}
      onChange={e => onChange(e.target.value)}
      size='small'
      displayEmpty
      sx={{
        minWidth, fontSize: '0.78rem', fontWeight: 500, background: '#fff', borderRadius: '8px',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ddd' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: PRIMARY },
        '& .MuiSelect-select': { py: '6px', px: '11px' },
      }}
    >
      {children}
    </Select>
  )
}
// MUI's Select clones each direct child and injects onClick/selected/etc — those
// props must reach the real MenuItem or clicking an option silently does nothing
// (the menu still closes via the backdrop, masking the failure). Forward ...rest.
const Mi = ({ value, children, ...rest }) => <MenuItem value={value} sx={{ fontSize: '0.8rem' }} {...rest}>{children}</MenuItem>

/* ─── Loading / error states ───────────────────────────────────────────────── */
function LoadingState() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, flexDirection: 'column', gap: 2 }}>
      <CircularProgress sx={{ color: PRIMARY }} size={36} />
      <Typography sx={{ color: '#9a9ab0', fontSize: '0.82rem' }}>Loading dashboard…</Typography>
    </Box>
  )
}

function ErrorState({ message }) {
  const isAccess      = message === 'ACCESS_DENIED'
  const isUnavailable = message === 'FEATURE_UNAVAILABLE'
  return (
    <Box sx={{ p: 4, textAlign: 'center', minHeight: 400 }}>
      <Typography sx={{ fontWeight: 700, color: isAccess ? RED : '#9a9ab0', mb: 1 }}>
        {isAccess ? 'Access denied — this account is not granted the Sales Dashboard module.'
          : isUnavailable ? 'Feature unavailable on this environment'
          : 'Failed to load dashboard'}
      </Typography>
      {!isAccess && !isUnavailable && (
        <Typography sx={{ fontSize: '0.8rem', color: '#bbb' }}>{message}</Typography>
      )}
    </Box>
  )
}

/* ─── Main ─────────────────────────────────────────────────────────────────── */
export default function LeadershipDashboard() {
  const [vm,        setVm]        = useState(null)
  const [vmError,   setVmError]   = useState(null)
  const [vmLoading, setVmLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setVmLoading(true)
    fetchVM()
      .then(data => { if (!cancelled) { setVm(data); setVmError(null) } })
      .catch(err  => { if (!cancelled) setVmError(err.message) })
      .finally(()  => { if (!cancelled) setVmLoading(false) })
    return () => { cancelled = true }
  }, [])

  // Clamp against stale ?tab=5 links (the old "Team & Mapping" tab, retired)
  // — a raw unvalidated value here renders a permanently blank panel since no
  // Tab/content branch matches '5' anymore.
  const [rawTab, setTab] = useTabParam('1')
  const tab = TABS.some(t => t.value === rawTab) ? rawTab : '1'
  const [seg,   setSeg]   = useState('ALL')
  const [kam,   setKam]   = useState('ALL')
  const [sam,   setSam]   = useState('ALL')
  const [chips, setChips] = useState(new Set())

  // goalPct comes from the API (server-config-driven); 10 is only the
  // pre-load fallback, matching the backend's own default.
  const filters = useMemo(
    () => ({ seg, kam, sam, chips, goal: vm?.goalPct ?? 10, search: '' }),
    [seg, kam, sam, chips, vm]
  )

  // (unmapped) is a real synthetic group from the API (shipping merchants with
  // no KAM/SAM/SL mapping yet) — it's not a pickable filter target, so it's
  // excluded here the same way the pre-existing (blank)/#N/A placeholders are.
  const kamOpts = useMemo(
    () => (vm?.kams || []).filter(k => k.name !== '(blank)' && k.name !== '(unmapped)'),
    [vm]
  )
  const samOpts = useMemo(() => {
    let list = (vm?.sams || []).filter(s => s.name && s.name !== '(blank)' && s.name !== '#N/A' && s.name !== '(unmapped)')
    if (kam !== 'ALL') list = list.filter(s => s.kam === kam)
    return list
  }, [vm, kam])

  const visibleChips = useMemo(() => {
    const hidden = HIDDEN_CHIPS_BY_TAB[tab] || []
    return CHIPS.filter(c => !hidden.includes(c.id))
  }, [tab])

  // Drop any chip this tab hides, so a filter can never stay applied while the
  // control that would let you see (and clear) it is gone.
  useEffect(() => {
    const hidden = HIDDEN_CHIPS_BY_TAB[tab] || []
    if (!hidden.length) return
    setChips(prev => {
      if (!hidden.some(id => prev.has(id))) return prev
      const next = new Set(prev)
      hidden.forEach(id => next.delete(id))
      return next
    })
  }, [tab])

  function toggleChip(id) {
    setChips(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })
  }
  function reset() { setSeg('ALL'); setKam('ALL'); setSam('ALL'); setChips(new Set()) }

  const { L = ['—','—','—'], S = ['—','—','—'] } = vm?.win || {}
  const [L0, L1, L2] = L; const [S0, S1, S2] = S
  const elapsed  = vm?.overall?.elapsed ?? 0
  const lastDate = (vm?.overall?.last3 || []).at(-1) || ''
  const showKamSam = tab === '1' || tab === '2' || tab === '4'
  const hasFilter  = seg !== 'ALL' || kam !== 'ALL' || sam !== 'ALL' || chips.size > 0

  return (
    <Page title=''>
      <PageTitle>
        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#1a1a2e' }}>
          Shipment Growth &amp; KAM / SAM Leadership
        </Typography>
      </PageTitle>

      <Box sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(145,158,171,0.24)', boxShadow: 'none' }}>

        {/* ── Header ───────────────────────────────────────────────── */}
        <Box sx={{
          background: '#fff',
          borderBottom: `3px solid ${PRIMARY}`,
          px: 2.5, py: 1.5,
          display: 'flex', alignItems: 'center', gap: 2,
        }}>
          {/* Left accent bar */}
          <Box sx={{ width: 4, height: 36, borderRadius: 2, background: PRIMARY, flexShrink: 0 }} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '0.98rem', color: '#1a1a2e', letterSpacing: '-0.01em', lineHeight: 1.2 }}>
              Prozo · Leadership Dashboard
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.4 }}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: 12, color: '#9a9ab0' }} />
              <Typography sx={{ fontSize: '0.68rem', color: '#9a9ab0' }}>
                Through {lastDate} &nbsp;·&nbsp; {elapsed} days elapsed &nbsp;·&nbsp; {L2}
              </Typography>
            </Box>
          </Box>

          {/* Status chips + reset */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0 }}>
            <Chip
              label={`${L2} · ${elapsed}d MTD`}
              size='small'
              sx={{ height: 20, fontSize: '0.6rem', fontWeight: 600, background: PRIMARY_LIGHT, color: PRIMARY, borderRadius: '6px' }}
            />
            <Tooltip title='Clear all filters' placement='bottom'>
              <Box
                onClick={reset}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.4, cursor: 'pointer',
                  fontSize: '0.72rem', fontWeight: 600,
                  color: hasFilter ? PRIMARY : '#ccc',
                  px: 1.25, py: 0.5, borderRadius: '8px',
                  border: `1px solid ${hasFilter ? PRIMARY_MID : '#DFE3E8'}`,
                  background: hasFilter ? PRIMARY_LIGHT : 'transparent',
                  transition: 'all 0.15s',
                  '&:hover': { background: PRIMARY_LIGHT, color: PRIMARY, borderColor: PRIMARY_MID },
                }}
              >
                <RestartAltIcon sx={{ fontSize: 14 }} /> Reset
              </Box>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Filter bar ── */}
        <Box sx={{ background: '#F4F6F8', px: 2.5, py: 1.1, borderBottom: '1px solid rgba(145,158,171,0.24)', display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
          <FilterAltOutlinedIcon sx={{ fontSize: 16, color: '#bbb', flexShrink: 0 }} />

          <FSelect value={seg} onChange={setSeg} minWidth={138}>
            <Mi value='ALL'>All segments</Mi>
            <Mi value='ENT'>Enterprise (ENT)</Mi>
            <Mi value='SME'>SME</Mi>
          </FSelect>

          {showKamSam && (
            <FSelect value={kam} onChange={v => { setKam(v); setSam('ALL') }} minWidth={172}>
              <Mi value='ALL'>All KAMs</Mi>
              {kamOpts.map(k => <Mi key={k.name} value={k.name}>{k.name} ({k.accts})</Mi>)}
            </FSelect>
          )}

          {showKamSam && (
            <FSelect value={sam} onChange={setSam} minWidth={168}>
              <Mi value='ALL'>All SAMs</Mi>
              {samOpts.map(s => <Mi key={s.name} value={s.name}>{displayName(s.name)} ({s.accts})</Mi>)}
            </FSelect>
          )}

          {/* Separator belongs to the chip group — without the guard it dangles
              on tabs where every chip is hidden. */}
          {visibleChips.length > 0 && (
            <Box sx={{ width: '1px', height: 20, background: '#ddd', mx: 0.25, flexShrink: 0 }} />
          )}

          {/* Quick chips */}
          {visibleChips.map(c => {
            const on = chips.has(c.id)
            return (
              <Box
                key={c.id}
                onClick={() => toggleChip(c.id)}
                sx={{
                  fontSize: '0.7rem', fontWeight: 600, px: 1.1, py: 0.4,
                  borderRadius: '20px', cursor: 'pointer', userSelect: 'none',
                  border: `1px solid ${on ? (c.danger ? RED : PRIMARY) : '#ddd'}`,
                  background: on ? (c.danger ? RED : PRIMARY) : '#fff',
                  color: on ? '#fff' : (c.danger ? RED : '#666'),
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: c.danger ? RED : PRIMARY, color: on ? '#fff' : (c.danger ? RED : PRIMARY), background: on ? undefined : c.danger ? '#FFE7D9' : PRIMARY_LIGHT },
                }}
              >
                {c.label}
              </Box>
            )
          })}
        </Box>

        {/* ── Tabs ─────────────────────────────────────────────────── */}
        <Box sx={{ background: '#fff', borderBottom: '1px solid #DFE3E8', px: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 40,
              '& .MuiTab-root': { fontWeight: 600, fontSize: '0.78rem', minHeight: 40, textTransform: 'none', color: '#888', px: 1.75, transition: 'color 0.2s' },
              '& .Mui-selected': { color: PRIMARY, fontWeight: 700 },
              '& .MuiTabs-indicator': { background: PRIMARY, height: 3, borderRadius: '3px 3px 0 0' },
            }}
          >
            {TABS.map((t, i) => (
              <Tab key={t.value} value={t.value} label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <Box sx={{
                    width: 16, height: 16, borderRadius: '50%', fontSize: '0.58rem', fontWeight: 800,
                    background: tab === t.value ? PRIMARY : '#DFE3E8',
                    color: tab === t.value ? '#fff' : '#888',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {i + 1}
                  </Box>
                  {t.label}
                </Box>
              } />
            ))}
          </Tabs>
        </Box>

        {/* ── Tab content ──────────────────────────────────────────── */}
        <Box sx={{ background: '#F4F6F8', p: 2, minHeight: 400 }}>
          {vmLoading && <LoadingState />}
          {!vmLoading && vmError && <ErrorState message={vmError} />}
          {!vmLoading && !vmError && vm && (
            <>
              {tab === '1' && <P1KamSam   vm={vm} filters={filters} L0={L0} L1={L1} L2={L2} S0={S0} S1={S1} S2={S2} />}
              {tab === '2' && <P2Shipments vm={vm} filters={filters} L0={L0} L1={L1} L2={L2} S0={S0} S1={S1} S2={S2} />}
              {tab === '3' && <P4Sales     vm={vm} filters={filters} L0={L0} L1={L1} L2={L2} S0={S0} S1={S1} S2={S2} />}
              {tab === '4' && <P5Top50     vm={vm} filters={filters} L0={L0} L1={L1} L2={L2} S0={S0} S1={S1} S2={S2} />}
            </>
          )}
        </Box>
      </Box>
    </Page>
  )
}
